import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from './contexts/AuthContext';
import { LoginForm } from './components/LoginForm';
import { YellowSDKClient, type ChannelState } from '@owb/yellow-sdk-frontend';
import { TournamentClient, type Tournament } from '@tournaments/tournament-client';
import { LoadingSpinner } from './components/LoadingSpinner';
import { ToastContainer, useToast } from './components/Toast';
import versionData from './version.json';
import './App.css';

// Log version on startup
if (typeof window !== 'undefined') {
  console.log(`🚀 Admin Dashboard v${versionData?.version ?? '?'} loaded`);
  console.log('📦 Version Data:', versionData);
}

// Winners Form Component
interface WinnersFormProps {
  tournament: Tournament;
  onClose: () => void;
  onSubmit: (winners: Array<{ address: string; position: number; prizeAmount: string }>) => Promise<void>;
  toast: ReturnType<typeof useToast>;
}

const WinnersForm: React.FC<WinnersFormProps> = ({ tournament, onSubmit, onClose, toast }) => {
  const [winners, setWinners] = useState<Array<{ address: string; position: number; prizeAmount: string }>>([]);
  const [distributionMode, setDistributionMode] = useState<'equal' | 'position'>('equal');

  const handleWinnerToggle = (address: string) => {
    setWinners(prev => {
      const existing = prev.find(w => w.address === address);
      if (existing) {
        return prev.filter(w => w.address !== address);
      }
      // Add new winner with position based on current count + 1
      const newPosition = prev.length + 1;
      return [...prev, { address, position: newPosition, prizeAmount: '0' }];
    });
  };

  const handlePositionChange = (address: string, newPosition: number) => {
    if (newPosition < 1 || newPosition > winners.length) {
      return; // Invalid position
    }

    setWinners(prev => {
      const oldWinner = prev.find(w => w.address === address);
      if (!oldWinner) return prev;

      const oldPosition = oldWinner.position;
      const updated = prev.map(w => {
        if (w.address === address) {
          return { ...w, position: newPosition };
        }
        // If another winner has this position, swap positions
        if (w.position === newPosition) {
          return { ...w, position: oldPosition };
        }
        return w;
      });
      // Sort by position
      return updated.sort((a, b) => a.position - b.position);
    });
  };

  const handlePrizeAmountChange = (address: string, prizeAmount: string) => {
    setWinners(prev => prev.map(w => 
      w.address === address ? { ...w, prizeAmount } : w
    ));
  };

  const calculatePrizePool = () => {
    return tournament.participants.reduce((sum, p) => {
      const amount = BigInt(p.depositAmount);
      return sum + amount;
    }, BigInt(0));
  };

  const calculateTotalPrizes = () => {
    return winners.reduce((sum, w) => {
      try {
        return sum + BigInt(w.prizeAmount || '0');
      } catch {
        return sum;
      }
    }, BigInt(0));
  };

  const applyDistributionPattern = (pattern: 'equal' | 'top3' | 'top5' | 'custom') => {
    if (winners.length === 0) {
      toast.warning('Please select winners first');
      return;
    }

    const prizePool = calculatePrizePool();
    let distribution: number[] = [];

    switch (pattern) {
      case 'equal':
        // Equal distribution
        distribution = Array(winners.length).fill(100 / winners.length);
        break;
      case 'top3':
        // Top 3: 50%, 30%, 20%
        if (winners.length >= 3) {
          distribution = [50, 30, 20, ...Array(winners.length - 3).fill(0)];
        } else {
          distribution = Array(winners.length).fill(100 / winners.length);
        }
        break;
      case 'top5':
        // Top 5: 40%, 25%, 15%, 10%, 10%
        if (winners.length >= 5) {
          distribution = [40, 25, 15, 10, 10, ...Array(winners.length - 5).fill(0)];
        } else if (winners.length === 4) {
          distribution = [40, 30, 20, 10];
        } else if (winners.length === 3) {
          distribution = [50, 30, 20];
        } else {
          distribution = Array(winners.length).fill(100 / winners.length);
        }
        break;
      default:
        return;
    }

    // Apply distribution
    const sortedWinners = [...winners].sort((a, b) => a.position - b.position);
    setWinners(sortedWinners.map((w, idx) => {
      const percentage = distribution[idx] || 0;
      const prizeAmount = (prizePool * BigInt(Math.floor(percentage * 100))) / BigInt(10000);
      return {
        ...w,
        position: idx + 1,
        prizeAmount: prizeAmount.toString(),
      };
    }));
  };

  const handleSubmit = async () => {
    const prizePool = calculatePrizePool();
    const winnerCount = winners.length;
    
    if (winnerCount === 0) {
      toast.warning('Please select at least one winner');
      return;
    }

    let finalWinners: Array<{ address: string; position: number; prizeAmount: string }>;

    if (distributionMode === 'equal') {
      // Equal distribution
      const prizePerWinner = (prizePool / BigInt(winnerCount)).toString();
      finalWinners = winners.map((w, idx) => ({
        ...w,
        position: idx + 1,
        prizeAmount: prizePerWinner,
      }));
    } else {
      // Position-based distribution
      // Validate positions are unique and sequential
      const positions = winners.map(w => w.position).sort((a, b) => a - b);
      const expectedPositions = Array.from({ length: winnerCount }, (_, i) => i + 1);
      
      if (JSON.stringify(positions) !== JSON.stringify(expectedPositions)) {
        toast.error('Positions must be unique and sequential starting from 1');
        return;
      }

      // Validate prize amounts are set
      for (const winner of winners) {
        if (!winner.prizeAmount || BigInt(winner.prizeAmount || '0') === 0n) {
          toast.error(`Please set prize amount for position ${winner.position}`);
          return;
        }
      }

      // Validate total prizes don't exceed prize pool
      const totalPrizes = calculateTotalPrizes();
      if (totalPrizes > prizePool) {
        toast.error(`Total prizes (${totalPrizes.toString()} wei) exceed prize pool (${prizePool.toString()} wei)`);
        return;
      }

      finalWinners = winners.map(w => ({
        ...w,
        prizeAmount: w.prizeAmount || '0',
      }));
    }

    await onSubmit(finalWinners);
  };

  const prizePool = calculatePrizePool();
  const totalPrizes = calculateTotalPrizes();
  const remaining = prizePool - totalPrizes;

  return (
    <div className="winners-form">
      <h4>Set Winners</h4>
      <p><strong>Prize Pool:</strong> {prizePool.toString()} wei</p>
      
      <div style={{ marginBottom: '15px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <strong>Distribution Mode:</strong>
            <select 
              value={distributionMode} 
              onChange={(e) => setDistributionMode(e.target.value as 'equal' | 'position')}
              style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #ddd' }}
            >
              <option value="equal">Equal Distribution</option>
              <option value="position">Position-Based</option>
            </select>
          </label>
          <button
            onClick={() => {
              // Randomly select winners (1-3 winners, or all if less than 3 participants)
              const numWinners = Math.min(
                Math.max(1, Math.floor(Math.random() * 3) + 1),
                tournament.participants.length
              );
              const shuffled = [...tournament.participants].sort(() => Math.random() - 0.5);
              const randomWinners = shuffled.slice(0, numWinners).map((p, idx) => ({
                address: p.address,
                position: idx + 1,
                prizeAmount: '0',
              }));
              setWinners(randomWinners);
              toast.success(`Randomly selected ${numWinners} winner(s)`);
            }}
            style={{ 
              padding: '5px 15px', 
              fontSize: '0.9em', 
              background: '#4caf50', 
              color: 'white',
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            🎲 Random Winners
          </button>
        </div>
        {distributionMode === 'equal' && (
          <p style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
            Prize pool will be divided equally among all selected winners
          </p>
        )}
        {distributionMode === 'position' && (
          <p style={{ fontSize: '0.9em', color: '#666', marginTop: '5px' }}>
            Set position and prize amount for each winner. Positions must be sequential (1, 2, 3...)
          </p>
        )}
      </div>

      <div style={{ marginBottom: '15px' }}>
        <h5 style={{ marginBottom: '10px' }}>
          Select Winners ({winners.length} selected):
        </h5>
        {winners.length > 0 && distributionMode === 'position' && (
          <div style={{ marginBottom: '10px', padding: '8px', background: '#e3f2fd', borderRadius: '4px', fontSize: '0.85em' }}>
            <strong>Selected Winners (sorted by position):</strong>
            <div style={{ marginTop: '5px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
              {[...winners].sort((a, b) => a.position - b.position).map((w) => (
                <div key={w.address} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span style={{ fontWeight: 'bold', minWidth: '20px' }}>#{w.position}</span>
                  <span style={{ flex: 1 }}>{w.address.substring(0, 10)}...</span>
                  <span style={{ fontFamily: 'monospace' }}>{w.prizeAmount || '0'} wei</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {tournament.participants.map((participant) => {
          const winner = winners.find(w => w.address === participant.address);
          const isSelected = !!winner;
          return (
            <div key={participant.address} style={{ marginBottom: '10px', padding: '10px', background: isSelected ? '#e8f5e9' : '#f5f5f5', borderRadius: '4px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleWinnerToggle(participant.address)}
                />
                <span style={{ flex: 1 }}>
                  {participant.address.substring(0, 10)}... - {participant.depositAmount} wei
                </span>
                {isSelected && distributionMode === 'position' && (
                  <span style={{ fontSize: '0.85em', color: '#666', fontFamily: 'monospace' }}>
                    Position: {winner.position} | Prize: {winner.prizeAmount || '0'} wei
                  </span>
                )}
              </label>
              
              {isSelected && distributionMode === 'position' && (
                <div style={{ marginTop: '10px', marginLeft: '25px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span style={{ fontSize: '0.9em' }}>Position:</span>
                    <input
                      type="number"
                      min="1"
                      max={winners.length}
                      value={winner.position}
                      onChange={(e) => {
                        const pos = parseInt(e.target.value);
                        if (pos >= 1 && pos <= winners.length) {
                          handlePositionChange(participant.address, pos);
                        }
                      }}
                      style={{ width: '60px', padding: '4px', borderRadius: '4px', border: '1px solid #ddd' }}
                    />
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span style={{ fontSize: '0.9em' }}>Prize (wei):</span>
                    <input
                      type="text"
                      value={winner.prizeAmount}
                      onChange={(e) => handlePrizeAmountChange(participant.address, e.target.value)}
                      placeholder="0"
                      style={{ width: '150px', padding: '4px', borderRadius: '4px', border: '1px solid #ddd', fontFamily: 'monospace' }}
                    />
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {distributionMode === 'position' && winners.length > 0 && (
        <div style={{ marginBottom: '15px' }}>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '0.9em', fontWeight: 'bold' }}>Quick Distribution Patterns:</label>
            <div style={{ display: 'flex', gap: '8px', marginTop: '5px', flexWrap: 'wrap' }}>
              <button 
                onClick={() => applyDistributionPattern('equal')}
                style={{ padding: '5px 10px', fontSize: '0.85em', background: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '4px', cursor: 'pointer' }}
              >
                Equal
              </button>
              <button 
                onClick={() => applyDistributionPattern('top3')}
                style={{ padding: '5px 10px', fontSize: '0.85em', background: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '4px', cursor: 'pointer' }}
              >
                Top 3 (50/30/20)
              </button>
              <button 
                onClick={() => applyDistributionPattern('top5')}
                style={{ padding: '5px 10px', fontSize: '0.85em', background: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '4px', cursor: 'pointer' }}
              >
                Top 5 (40/25/15/10/10)
              </button>
            </div>
          </div>
          <div style={{ padding: '10px', background: '#fff3cd', borderRadius: '4px', fontSize: '0.9em' }}>
            <p><strong>Total Prizes:</strong> {totalPrizes.toString()} wei</p>
            <p><strong>Remaining:</strong> {remaining >= 0n ? remaining.toString() : '0'} wei</p>
            {remaining < 0n && (
              <p style={{ color: '#f44336', fontWeight: 'bold' }}>
                ⚠️ Total prizes exceed prize pool by {(-remaining).toString()} wei
              </p>
            )}
            {remaining > 0n && (
              <p style={{ color: '#666', fontSize: '0.85em' }}>
                💡 {remaining.toString()} wei will remain unallocated
              </p>
            )}
          </div>
        </div>
      )}

      {distributionMode === 'equal' && winners.length > 0 && (
        <div style={{ marginBottom: '15px', padding: '10px', background: '#e3f2fd', borderRadius: '4px', fontSize: '0.9em' }}>
          <p><strong>Winners:</strong> {winners.length}</p>
          <p><strong>Prize per Winner:</strong> {(prizePool / BigInt(winners.length)).toString()} wei</p>
        </div>
      )}

      <div className="winners-form-actions">
        <button onClick={handleSubmit}>Submit Winners</button>
        <button onClick={onClose} style={{ background: '#f44336' }}>Cancel</button>
      </div>
    </div>
  );
};

// HARDCODED: Always use backend URL directly (never proxy)
const getApiUrl = () => {
  // Check if we're on localhost for local development

   
 // console.log('getApiUrl', 'https://tournaments-backend.fawesome-hypercasual.workers.dev');

  // HARDCODED: Always use backend URL directly (never proxy)
  return 'https://tournaments-backend.fawesome-hypercasual.workers.dev';
};

///аккуратно отделить все использование yellowsdk от остального кода

// Initialize Yellow SDK client with Privy token getter
// HARDCODED: Always uses backend URL directly (never proxy)
function createYellowSDKClient(getAccessToken: () => Promise<string | null>) {
  const apiUrl = getApiUrl();
  // Throttle token requests to prevent 429 errors (max 1 request per 2 seconds)
  let lastTokenCall = 0;
  const throttledGetToken = async (): Promise<string | null> => {
    const now = Date.now();
    const timeSinceLastCall = now - lastTokenCall;
    if (timeSinceLastCall < 2000) {
      await new Promise(resolve => setTimeout(resolve, 2000 - timeSinceLastCall));
    }
    lastTokenCall = Date.now();
    return getAccessToken();
  };
  return new YellowSDKClient({
    apiUrl: apiUrl,
    getAccessToken: throttledGetToken,
  });
}

// Initialize Tournament client with JWT token getter
function createTournamentClient(getAccessToken: () => Promise<string | null>) {
  const apiUrl = getApiUrl();
  // Throttle token requests to prevent 429 errors (max 1 request per 2 seconds)
  let lastTokenCall = 0;
  const throttledGetToken = async (): Promise<string | null> => {
    const now = Date.now();
    const timeSinceLastCall = now - lastTokenCall;
    if (timeSinceLastCall < 2000) {
      await new Promise(resolve => setTimeout(resolve, 2000 - timeSinceLastCall));
    }
    lastTokenCall = Date.now();
    
    console.log('[createTournamentClient] Getting access token...');
    const token = await getAccessToken();
    console.log('[createTournamentClient] Token retrieved:', {
      hasToken: !!token,
      tokenLength: token?.length || 0,
      tokenPrefix: token ? token.substring(0, 20) + '...' : 'null',
    });
    return token;
  };
  return new TournamentClient({
    apiUrl: apiUrl,
    getAccessToken: throttledGetToken,
  });
}

// Tournament and ChannelState interfaces are now imported from @tournaments/yellow-sdk-frontend

function App() {
  const { user, logout, isAuthenticated, isLoading, getAccessToken } = useAuth();
  const yellowSDK = useMemo(() => createYellowSDKClient(getAccessToken), [getAccessToken]);
  const tournamentClient = useMemo(() => createTournamentClient(getAccessToken), [getAccessToken]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingTournaments, setLoadingTournaments] = useState(false);
  const [channelState, setChannelState] = useState<ChannelState | null>(null);
  const [newTournament, setNewTournament] = useState({ name: '', entryFee: '', ticketPrice: '1000' });
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const toast = useToast();


  useEffect(() => {
    if (isAuthenticated && user) {
      // Check if user is admin
      console.log('[Admin Check] User data:', {
        id: user.id,
        username: user.username,
        email: user.email,
        isAdmin: user.isAdmin,
        isAdminType: typeof user.isAdmin,
        isAdminValue: user.isAdmin === true ? 'true' : user.isAdmin === false ? 'false' : 'undefined/null',
      });
      
      const isUserAdmin = user.isAdmin || false;
      console.log('[Admin Check] Admin status determination:', {
        userIsAdmin: user.isAdmin,
        isUserAdmin,
        willGrantAccess: isUserAdmin === true,
      });
      
      setIsAdmin(isUserAdmin);
      
      if (isUserAdmin) {
        console.log('[Admin Check] ✅ Access granted - loading dashboard');
        loadTournaments();
        loadChannelState();
        // Only refresh channel state if it's not a 503 error (Yellow SDK not connected)
        let shouldRefreshChannelState = true;
        let lastTournamentLoad = 0;
        let lastChannelStateLoad = 0;
        
        const interval = setInterval(() => {
          const now = Date.now();
          // Throttle tournament loading - max once per 10 seconds
          if (now - lastTournamentLoad >= 10000) {
            lastTournamentLoad = now;
            loadTournaments(false);
          }
          // Throttle channel state loading - max once per 15 seconds
          if (shouldRefreshChannelState && now - lastChannelStateLoad >= 15000) {
            lastChannelStateLoad = now;
            loadChannelState().catch((error: any) => {
              // Stop refreshing channel state if it's a persistent 503
              if (error?.response?.status === 503 || error?.message?.includes('Yellow SDK not connected')) {
                shouldRefreshChannelState = false;
              }
            });
          }
        }, 10000); // Check every 10 seconds
        return () => clearInterval(interval);
      } else {
        console.log('[Admin Check] ❌ Access denied - user is not admin');
        console.log('[Admin Check] User details:', {
          username: user.username,
          email: user.email,
          id: user.id,
          isAdmin: user.isAdmin,
        });
      }
    } else {
      console.log('[Admin Check] Not authenticated or no user:', {
        isAuthenticated,
        hasUser: !!user,
      });
      setIsAdmin(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  const loadTournaments = async (showLoading = false) => {
    try {
      if (!tournamentClient) {
        toast.error('Tournament client not initialized');
        return;
      }
      if (showLoading) {
        setLoadingTournaments(true);
      }
      console.log('[loadTournaments] Fetching tournaments...');
      const tournaments = await tournamentClient.getTournaments();
      console.log('[loadTournaments] Successfully loaded tournaments:', tournaments.length);
      setTournaments(tournaments);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to load tournaments';
      console.error('[loadTournaments] Error details:', {
        message: error?.message,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        url: error?.config?.url,
        baseURL: error?.config?.baseURL,
        fullError: error,
      });
      toast.error(errorMessage);
      console.error('Failed to load tournaments:', error);
    } finally {
      setLoading(false);
      setLoadingTournaments(false);
    }
  };

  const loadChannelState = async () => {
    try {
      if (!yellowSDK) {
        return;
      }
      const state = await yellowSDK.getChannelState();
      setChannelState(state);
    } catch (error: any) {
      // Only show error if it's not a 503 (Yellow SDK not connected)
      if (error?.response?.status !== 503 && !error?.message?.includes('Yellow SDK not connected')) {
        const errorMessage = error?.response?.data?.error || error?.message || 'Failed to load channel state';
        toast.warning(errorMessage);
      }
      console.error('Failed to load channel state:', error);
    }
  };

  const createTournament = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Debug: Check token before making request
      console.log('[createTournament] About to create tournament, checking token...');
      const token = await getAccessToken();
      console.log('[createTournament] Token retrieved:', {
        hasToken: !!token,
        tokenLength: token?.length || 0,
        tokenPrefix: token ? token.substring(0, 20) + '...' : 'null',
      });

      await tournamentClient.createTournament(newTournament.name, newTournament.entryFee, newTournament.ticketPrice);
      setNewTournament({ name: '', entryFee: '', ticketPrice: '1000' });
      toast.success('Tournament created successfully!');
      loadTournaments(true);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to create tournament';
      toast.error(errorMessage);
      console.error('[createTournament] Tournament creation failed:', error);
    }
  };

  const startTournament = async (tournamentId: string) => {
    try {
      await tournamentClient.startTournament(tournamentId);
      toast.success('Tournament started successfully!');
      loadTournaments(true);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to start tournament';
      toast.error(errorMessage);
    }
  };

  const determineWinners = async (tournamentId: string, winners: Array<{ address: string; position: number; prizeAmount: string }>) => {
    try {
      await tournamentClient.determineWinners(tournamentId, winners);
      toast.success('Winners determined successfully!');
      loadTournaments(true);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to determine winners';
      toast.error(errorMessage);
    }
  };

  const closeTournament = async (tournamentId: string) => {
    try {
      await tournamentClient.closeTournament(tournamentId);
      toast.success('Tournament closed successfully!');
      loadTournaments(true);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to close tournament';
      toast.error(errorMessage);
    }
  };

  if (isLoading) {
    return (
      <>
        <LoadingSpinner fullScreen message="Initializing..." />
        <div style={{ position: 'fixed', bottom: '10px', right: '10px', fontSize: '12px', color: '#666', background: 'rgba(255,255,255,0.8)', padding: '4px 8px', borderRadius: '4px', zIndex: 1000 }}>
          admin-dashboard v{versionData?.version ?? '?'}
        </div>
      </>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <LoginForm />
        <div style={{ position: 'fixed', bottom: '10px', right: '10px', fontSize: '12px', color: '#666', background: 'rgba(255,255,255,0.8)', padding: '4px 8px', borderRadius: '4px', zIndex: 1000 }}>
          admin-dashboard v{versionData?.version ?? '?'}
        </div>
      </>
    );
  }

  // Check if user is authorized admin
  if (isAuthenticated && user && isAdmin === false) {
    console.error('[Access Denied] Detailed debug info:', {
      isAuthenticated,
      hasUser: !!user,
      userData: user,
      isAdmin,
      isAdminType: typeof isAdmin,
      userIsAdmin: user.isAdmin,
      userIsAdminType: typeof user.isAdmin,
      checkResult: {
        'user.isAdmin === true': user.isAdmin === true,
        'user.isAdmin === false': user.isAdmin === false,
        'user.isAdmin == true': user.isAdmin == true, // eslint-disable-line eqeqeq
        'user.isAdmin == false': user.isAdmin == false, // eslint-disable-line eqeqeq
        '!!user.isAdmin': !!user.isAdmin,
        'user.isAdmin || false': user.isAdmin || false,
      },
    });
    
    return (
      <div className="App">
        <header className="App-header">
          <h1>Tournament Admin Dashboard</h1>
          <p>Access Denied</p>
        </header>
        <div className="container">
          <div className="welcome" style={{ textAlign: 'center', padding: '2rem' }}>
            <h2>⚠️ Access Denied</h2>
            <p>You are authenticated but not authorized to access the admin dashboard.</p>
            <div style={{ 
              marginTop: '1rem', 
              padding: '1rem', 
              background: '#f5f5f5', 
              borderRadius: '4px', 
              textAlign: 'left',
              fontSize: '0.85em',
              fontFamily: 'monospace',
            }}>
              <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold' }}>Debug Information:</p>
              <p style={{ margin: '0.25rem 0' }}>Username: <code>{user.username}</code></p>
              <p style={{ margin: '0.25rem 0' }}>Email: <code>{user.email || 'N/A'}</code></p>
              <p style={{ margin: '0.25rem 0' }}>User ID: <code>{user.id}</code></p>
              <p style={{ margin: '0.25rem 0' }}>isAdmin (raw): <code>{String(user.isAdmin)}</code></p>
              <p style={{ margin: '0.25rem 0' }}>isAdmin (type): <code>{typeof user.isAdmin}</code></p>
              <p style={{ margin: '0.25rem 0' }}>isAdmin (strict true): <code>{String(user.isAdmin === true)}</code></p>
              <p style={{ margin: '0.25rem 0' }}>isAdmin (strict false): <code>{String(user.isAdmin === false)}</code></p>
              <p style={{ margin: '0.25rem 0', fontSize: '0.8em', color: '#999', marginTop: '0.5rem' }}>
                Check browser console for detailed logs
              </p>
            </div>
            <p style={{ fontSize: '0.9em', color: '#666', marginTop: '1rem' }}>
              Please contact an administrator to be added to the allowed admins list.
            </p>
            <button onClick={logout} style={{ marginTop: '1rem', background: '#f44336' }}>
              Logout
            </button>
          </div>
        </div>
        <div style={{ position: 'fixed', bottom: '10px', right: '10px', fontSize: '12px', color: '#666', background: 'rgba(255,255,255,0.8)', padding: '4px 8px', borderRadius: '4px', zIndex: 1000 }}>
          admin-dashboard v{versionData?.version ?? '?'}
        </div>
      </div>
    );
  }

  if (loading || isAdmin === null) {
    return <LoadingSpinner fullScreen message="Loading dashboard..." />;
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>Tournament Admin Dashboard</h1>
        {channelState?.channelId && (
          <p className="channel-id-header">Channel ID: {channelState.channelId}</p>
        )}
        {isAuthenticated && isAdmin && user && (
          <div className="user-info">
            <p>Admin: {user.username}</p>
            <button onClick={logout}>Logout</button>
          </div>
        )}
      </header>

      <div className="container">
        {/* Channel State */}
        <section className="channel-state">
          <h2>Yellow Channel State</h2>
          {channelState ? (
            <div>
              <p><strong>Custody Balance:</strong> {channelState.custodyBalance} wei</p>
            </div>
          ) : (
            <p>Loading channel state...</p>
          )}
        </section>

        {/* Create Tournament */}
        <section className="create-tournament">
          <h2>Create New Tournament</h2>
          <form onSubmit={createTournament}>
            <input
              type="text"
              placeholder="Tournament Name"
              value={newTournament.name}
              onChange={(e) => setNewTournament({ ...newTournament, name: e.target.value })}
              required
            />
            <input
              type="text"
              placeholder="Entry Fee (wei)"
              value={newTournament.entryFee}
              onChange={(e) => setNewTournament({ ...newTournament, entryFee: e.target.value })}
              required
            />
            <input
              type="text"
              placeholder="Ticket Price (wei)"
              value={newTournament.ticketPrice}
              onChange={(e) => setNewTournament({ ...newTournament, ticketPrice: e.target.value })}
            />
            <button type="submit">Create Tournament</button>
          </form>
        </section>

        {/* Tournaments List */}
        <section className="tournaments">
          <h2>Tournaments ({tournaments.length})</h2>
          {loadingTournaments ? (
            <LoadingSpinner message="Loading tournaments..." />
          ) : (
            <div className="tournament-list">
              {tournaments.map((tournament) => (
              <div key={tournament.id} className="tournament-card">
                <h3>{tournament.name}<span>(ID: {tournament.id.substring(0, 8)}...)</span></h3>
                <p><strong>Status:</strong> {tournament.status}</p>
                <p><strong>Entry Fee:</strong> {tournament.entryFee} wei</p>
                <p><strong>Ticket Price:</strong> {tournament.ticketPrice || '1000'} wei</p>
                <p><strong>Ticket Count:</strong> {tournament.entryFee && tournament.ticketPrice ? (BigInt(tournament.entryFee) / BigInt(tournament.ticketPrice || '1000')).toString() : '0'}</p>
                <p><strong>Prize Pool:</strong> {tournament.prizePool} wei</p>
                <p><strong>Participants:</strong> {tournament.participants.length}</p>
                {tournament.channelId && (
                  <>
                    <p><strong>Channel ID:</strong> {tournament.channelId}</p>
                    {channelState && channelState.channelId === tournament.channelId && (
                      <p><strong>Channel Balance:</strong> {channelState.custodyBalance} wei</p>
                    )}
                  </>
                )}
                
                {tournament.status === 'completed' && tournament.winners.length > 0 && (
                  <div style={{ marginTop: '15px', padding: '10px', background: '#e8f5e9', borderRadius: '4px' }}>
                    <h4 style={{ marginTop: 0, marginBottom: '10px' }}>🏆 Winners</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      {[...tournament.winners].sort((a, b) => a.position - b.position).map((winner) => (
                        <div key={winner.address} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px', background: '#fff', borderRadius: '3px' }}>
                          <span>
                            <strong>#{winner.position}</strong> {winner.address.substring(0, 10)}...{winner.address.substring(winner.address.length - 8)}
                          </span>
                          <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{winner.prizeAmount} wei</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="tournament-actions">
                  {tournament.status === 'open' && (
                    <button onClick={() => startTournament(tournament.id)}>
                      Start Tournament
                    </button>
                  )}
                  {tournament.status === 'active' && (
                    <>
                      <button onClick={() => setSelectedTournament(tournament)}>
                        Set Winners
                      </button>
                      <button onClick={() => closeTournament(tournament.id)}>
                        Close Tournament
                      </button>
                    </>
                  )}
                </div>

                {selectedTournament?.id === tournament.id && (
                  <WinnersForm
                    tournament={tournament}
                    onClose={() => setSelectedTournament(null)}
                    onSubmit={async (winners) => {
                      await determineWinners(tournament.id, winners);
                      setSelectedTournament(null);
                    }}
                    toast={toast}
                  />
                )}
              </div>
                ))}
            </div>
          )}
        </section>
      </div>
      <ToastContainer toasts={toast.toasts} onClose={toast.removeToast} />
      <div style={{ position: 'fixed', bottom: '10px', right: '10px', fontSize: '12px', color: '#666', background: 'rgba(255,255,255,0.8)', padding: '4px 8px', borderRadius: '4px', zIndex: 1000 }}>
        admin-dashboard v{versionData?.version ?? '?'}
      </div>
    </div>
  );
}

export default App;

